import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { GraduationCap } from "lucide-react";


export const Route = createFileRoute("/_authenticated/attendance")({
  head: () => ({ meta: [{ title: "Absensi — MI Al-Munajah Admin" }] }),
  component: AttendancePage,
});

type Status = "hadir" | "izin" | "sakit" | "alpha";
type Student = { id: string; full_name: string; class_id: string | null; photo_url: string | null };
type Class = { id: string; name: string };
type Att = { student_id: string; date: string; status: Status };

const statusColor: Record<Status, string> = {
  hadir: "bg-success text-white",
  izin: "bg-info text-white",
  sakit: "bg-warning text-black",
  alpha: "bg-destructive text-white",
};

function AttendancePage() {
  const qc = useQueryClient();
  const [view, setView] = useState<"harian" | "rekap">("harian");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [classId, setClassId] = useState<string>("");
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));

  // ---- Classes (cached) ----
  const { data: classes = [] } = useQuery({
    queryKey: ["attendance", "classes"],
    queryFn: async () => {
      const { data } = await supabase.from("classes").select("id,name").order("name");
      return (data || []) as Class[];
    },
  });

  useEffect(() => {
    if (classes.length && !classId) setClassId(classes[0].id);
  }, [classes, classId]);

  // ---- Students per class (cached) ----
  const { data: students = [] } = useQuery({
    queryKey: ["attendance", "students", classId],
    enabled: !!classId,
    queryFn: async () => {
      const { data } = await supabase
        .from("students")
        .select("id,full_name,class_id,photo_url")
        .eq("class_id", classId)
        .order("full_name");
      return (data || []) as Student[];
    },
  });

  const studentIds = useMemo(() => students.map((s) => s.id), [students]);

  // ---- Daily attendance (cached) ----
  const { data: dayRows = [] } = useQuery({
    queryKey: ["attendance", "day", date, classId, studentIds.length],
    enabled: !!classId && studentIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from("attendance")
        .select("student_id,date,status")
        .eq("date", date)
        .in("student_id", studentIds);
      return (data || []) as Att[];
    },
  });

  const att = useMemo(() => {
    const m = new Map<string, Status>();
    dayRows.forEach((r) => m.set(r.student_id, r.status));
    return m;
  }, [dayRows]);

  // ---- Monthly recap (cached) ----
  const { data: monthData = [] } = useQuery({
    queryKey: ["attendance", "month", month, classId, studentIds.length],
    enabled: view === "rekap" && !!classId && studentIds.length > 0,
    queryFn: async () => {
      const start = `${month}-01`;
      const end = new Date(new Date(start).getFullYear(), new Date(start).getMonth() + 1, 1)
        .toISOString()
        .slice(0, 10);
      const { data } = await supabase
        .from("attendance")
        .select("student_id,date,status")
        .gte("date", start)
        .lt("date", end)
        .in("student_id", studentIds);
      return (data || []) as Att[];
    },
  });

  // ---- Realtime sync (does not refetch; patches cache directly) ----
  useEffect(() => {
    const channel = supabase
      .channel("attendance-sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "attendance" }, (payload) => {
        const row = (payload.new ?? payload.old) as Att | undefined;
        if (!row) return;
        // Invalidate any cached attendance queries that may include this row.
        qc.invalidateQueries({ queryKey: ["attendance", "day"] });
        qc.invalidateQueries({ queryKey: ["attendance", "month"] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "students" }, () => {
        qc.invalidateQueries({ queryKey: ["attendance", "students"] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "classes" }, () => {
        qc.invalidateQueries({ queryKey: ["attendance", "classes"] });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc]);

  const setStatus = async (studentId: string, status: Status) => {
    const key = ["attendance", "day", date, classId, studentIds.length];
    const prev = qc.getQueryData<Att[]>(key) || [];
    const next = (() => {
      const idx = prev.findIndex((r) => r.student_id === studentId);
      if (idx >= 0) {
        const copy = prev.slice();
        copy[idx] = { ...copy[idx], status };
        return copy;
      }
      return [...prev, { student_id: studentId, date, status }];
    })();
    qc.setQueryData(key, next);
    const { error } = await supabase
      .from("attendance")
      .upsert({ student_id: studentId, date, status }, { onConflict: "student_id,date" });
    if (error) {
      toast.error(error.message);
      qc.setQueryData(key, prev);
    }
  };

  const recap = useMemo(() => {
    const map: Record<string, { hadir: number; izin: number; sakit: number; alpha: number }> = {};
    students.forEach((s) => {
      map[s.id] = { hadir: 0, izin: 0, sakit: 0, alpha: 0 };
    });
    monthData.forEach((r) => {
      if (map[r.student_id]) map[r.student_id][r.status]++;
    });
    return map;
  }, [students, monthData]);

  return (
    <div>
      <PageHeader title="Absensi" description="Catat dan rekap kehadiran siswa per bulan." />

      <Card className="p-2.5 sm:p-4 rounded-2xl mb-3 space-y-2">
        <Tabs value={view} onValueChange={(v) => setView(v as "harian" | "rekap")} className="w-full">
          <TabsList className="w-full grid grid-cols-2 h-9 p-1 rounded-lg bg-muted/60">
            <TabsTrigger value="harian" className="rounded-md text-xs font-medium">Harian</TabsTrigger>
            <TabsTrigger value="rekap" className="rounded-md text-xs font-medium">Rekap Bulanan</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="grid grid-cols-2 gap-2">
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger className="w-full h-9 rounded-lg bg-surface-variant/50 border-border/60 text-xs px-2.5 gap-1.5">
              <GraduationCap className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
              <SelectValue placeholder="Pilih kelas">{classes.find(c => c.id === classId) ? `Kelas ${classes.find(c => c.id === classId)!.name}` : undefined}</SelectValue>
            </SelectTrigger>
            <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>Kelas {c.name}</SelectItem>)}</SelectContent>
          </Select>

          {view === "harian" ? (
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full h-9 rounded-lg bg-surface-variant/50 border-border/60 text-xs px-2.5" />
          ) : (
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-full h-9 rounded-lg bg-surface-variant/50 border-border/60 text-xs px-2.5" />
          )}
        </div>
      </Card>




      <Card className="rounded-2xl overflow-hidden">
        {students.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-sm">Pilih kelas yang memiliki siswa</div>
        ) : view === "harian" ? (
          <div className="divide-y">
            {students.map((s) => {
              const st = att.get(s.id);
              return (
                <div key={s.id} className="p-3 sm:p-4">
                  <div className="flex sm:items-center gap-3 flex-col sm:flex-row">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <Avatar className="w-9 h-9 shrink-0">
                        {s.photo_url && <AvatarImage src={s.photo_url} />}
                        <AvatarFallback className="bg-primary/15 text-primary text-xs">{s.full_name.split(" ").map(x=>x[0]).slice(0,2).join("")}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
                        <div className="font-medium text-sm break-words sm:truncate">{s.full_name}</div>
                        {(() => {
                          const cls = classes.find((c) => c.id === s.class_id);
                          return cls ? (
                            <Badge variant="outline" className="rounded-full text-[10px] px-2 py-0.5 shrink-0 bg-primary/10 text-primary border-primary/20 font-medium">
                              Kelas {cls.name}
                            </Badge>
                          ) : null;
                        })()}
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-1.5 sm:flex sm:gap-1 sm:flex-wrap sm:justify-end">
                      {(["hadir", "izin", "sakit", "alpha"] as Status[]).map((opt) => (
                        <Button key={opt} size="sm" variant={st === opt ? "default" : "outline"}
                          className={`rounded-full capitalize text-xs h-8 w-full sm:w-auto ${st === opt ? statusColor[opt] : ""}`}
                          onClick={() => setStatus(s.id, opt)}>{opt}</Button>
                      ))}
                    </div>
                  </div>
                </div>

              );
            })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-variant">
                <tr className="text-left">
                  <th className="p-3 font-medium">Siswa</th>
                  <th className="p-3 font-medium text-center">Hadir</th>
                  <th className="p-3 font-medium text-center">Izin</th>
                  <th className="p-3 font-medium text-center">Sakit</th>
                  <th className="p-3 font-medium text-center">Alpha</th>
                  <th className="p-3 font-medium text-center">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {students.map((s) => {
                  const r = recap[s.id] || { hadir: 0, izin: 0, sakit: 0, alpha: 0 };
                  const total = r.hadir + r.izin + r.sakit + r.alpha;
                  return (
                    <tr key={s.id}>
                      <td className="p-3 font-medium">{s.full_name}</td>
                      <td className="p-3 text-center"><Badge className="bg-success/15 text-success border-0 rounded-full">{r.hadir}</Badge></td>
                      <td className="p-3 text-center"><Badge className="bg-info/15 text-info border-0 rounded-full">{r.izin}</Badge></td>
                      <td className="p-3 text-center"><Badge className="bg-warning/15 text-warning border-0 rounded-full">{r.sakit}</Badge></td>
                      <td className="p-3 text-center"><Badge className="bg-destructive/15 text-destructive border-0 rounded-full">{r.alpha}</Badge></td>
                      <td className="p-3 text-center font-semibold">{total}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
